//Animal selector

import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Animal } from '../../types';

interface AnimalSelectorProps {
  animals: Animal[];
  selectedAnimalId: string;
  onSelect: (animalId: string) => void;
  error?: string;
}

const AnimalSelector: React.FC<AnimalSelectorProps> = ({
  animals,
  selectedAnimalId,
  onSelect,
  error,
}) => {
  const [hoveredAnimal, setHoveredAnimal] = useState<string | null>(null);

  if (animals.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-powder-500">Loading animals...</p>
      </div>
    );
  }

  return (
    <div className="space-y-4 pb-20">
      <div className="grid grid-cols-1 gap-4">
        {animals.map((animal) => (
          <motion.div
            key={animal.id}
            className={`relative p-4 rounded-xl border-2 cursor-pointer transition-all duration-200 ${
              selectedAnimalId === animal.id
                ? 'border-sage-500 bg-sage-50 shadow-lg'
                : 'border-powder-200 bg-white hover:border-sage-300 hover:shadow-md'
            } ${hoveredAnimal === animal.id ? 'z-40' : 'z-10'}`}
            onClick={() => onSelect(animal.id)}
            onMouseEnter={() => setHoveredAnimal(animal.id)}
            onMouseLeave={() => setHoveredAnimal(null)}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <div className="flex items-center space-x-4">
              <div className="flex-shrink-0">
                <img
                  src={animal.imagePath}
                  alt={animal.name}
                  className="w-16 h-16 rounded-full object-cover border-2 border-powder-200"
                  onError={(e) => {
                    //Fallback for missing images
                    const target = e.target as HTMLImageElement;
                    target.src = `data:image/svg+xml;base64,${btoa(`
                      <svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="32" cy="32" r="30" fill="#E2E8F0"/>
                        <text x="32" y="38" text-anchor="middle" font-family="Arial, sans-serif" font-size="12" fill="#64748B">
                          ${animal.name[0]}
                        </text>
                      </svg>
                    `)}`;
                  }}
                />
              </div>
              
              <div className="flex-1">
                <h4 className="font-semibold text-powder-800">{animal.name}</h4>
                <p className="text-sm text-powder-600">{animal.shortDescription}</p>
              </div>

              {selectedAnimalId === animal.id && (
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  className="flex-shrink-0"
                >
                  <div className="w-6 h-6 bg-sage-500 rounded-full flex items-center justify-center">
                    <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                </motion.div>
              )}
            </div>

            {/* Tooltip with long description */}
            <AnimatePresence>
              {hoveredAnimal === animal.id && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 10 }}
                  className="absolute z-50 top-full right-0 mt-2 p-4 bg-powder-800 text-white rounded-lg shadow-xl w-4/5 ml-auto"
                >
                  <p className="text-sm leading-relaxed">{animal.longDescription}</p>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        ))}
      </div>

      {error && (
        <motion.p
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-sm text-red-600 mt-2"
        >
          {error}
        </motion.p>
      )}
    </div>
  );
};

export default AnimalSelector;
